"use client";

import { Spinner } from "@/components/ui/spinner";

import { useEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useObjectUrlLifecycle } from "./multi-file-object-url";

interface PdfFilePreviewProps {
  fileName: string;
  loadFile: () => Promise<Blob | File | null>;
  disabled?: boolean;
  onError?: (message: string) => void;
}

const PDF_SIGNATURE = "%PDF-";

async function verifiedPdfBlob(file: Blob | File): Promise<Blob> {
  const signature = new TextDecoder("ascii").decode(
    await file.slice(0, PDF_SIGNATURE.length).arrayBuffer(),
  );
  if (signature !== PDF_SIGNATURE) {
    throw new Error("El archivo seleccionado no contiene un PDF válido");
  }
  return file.type === "application/pdf"
    ? file
    : new Blob([file], { type: "application/pdf" });
}

export function PdfFilePreview({
  fileName,
  loadFile,
  disabled = false,
  onError,
}: PdfFilePreviewProps) {
  const [loading, setLoading] = useState(false);
  const previewPollRef = useRef<number | null>(null);
  const { replaceObjectUrl, releaseObjectUrl } = useObjectUrlLifecycle();

  useEffect(() => {
    return () => {
      if (previewPollRef.current !== null) {
        window.clearTimeout(previewPollRef.current);
      }
    };
  }, []);

  const releasePreviewUrl = () => {
    if (previewPollRef.current !== null) {
      window.clearTimeout(previewPollRef.current);
      previewPollRef.current = null;
    }
    releaseObjectUrl();
  };

  const releaseWhenPreviewCloses = (previewWindow: Window) => {
    const checkClosed = () => {
      if (previewWindow.closed) {
        previewPollRef.current = null;
        releaseObjectUrl();
        return;
      }
      previewPollRef.current = window.setTimeout(checkClosed, 250);
    };
    checkClosed();
  };

  const handlePreview = async () => {
    releasePreviewUrl();
    const previewWindow = window.open("about:blank", "_blank");
    if (!previewWindow) {
      onError?.("El navegador bloqueó la pestaña de previsualización");
      return;
    }
    previewWindow.opener = null;
    setLoading(true);
    try {
      const file = await loadFile();
      if (!file) {
        previewWindow.close();
      } else {
        const url = replaceObjectUrl(await verifiedPdfBlob(file));
        previewWindow.location.replace(url);
        releaseWhenPreviewCloses(previewWindow);
      }
    } catch (error) {
      previewWindow.close();
      releasePreviewUrl();
      onError?.(
        error instanceof Error ? error.message : "No se pudo visualizar el archivo",
      );
    }
    setLoading(false);
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={() => void handlePreview()}
      disabled={disabled || loading}
      aria-label={`Previsualizar ${fileName}`}
      title="Previsualizar"
    >
      {loading ? <Spinner /> : <Eye />}
    </Button>
  );
}
